/**
 * GroupLiveInitiate — Practitioner screen to start a group live WEMWBS session.
 *
 * VER-56 Model A: Practitioner initiates, participants join on their own devices.
 *
 * MOCK-FIRST: No backend calls. Clicking "Start session" generates a local mock
 * session code and transitions to the lobby view via the onSessionStarted callback.
 *
 * TODOs:
 * - TODO [RBAC]: Replace permission guard placeholder with the correct permit
 *   code once defined (e.g. "AssessGroupStart"). Run verbal-rbac-review before merge.
 * - TODO [VER-55]: Replace the mock session creation with a POST to
 *   /api/group-assessments/sessions (see VER-56-NOTES.md for API contract).
 * - TODO [VER-55]: Wire sessionName + participantCount to the request body.
 */

import React, { useState } from "react";
import { Alert, Box, Button, Card, CardContent, Divider, TextField, Typography } from "@mui/material";
import { GroupOutlined, PlayArrowOutlined } from "@mui/icons-material";
import { useMobile } from "../../../hooks/useMobile";
import { MOCK_LIVE_SESSION, GroupLiveSession } from "./groupLive.mock";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type GroupLiveInitiateProps = {
    /** Called after the practitioner confirms. Passes the newly created session. */
    onSessionStarted: (session: GroupLiveSession) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GroupLiveInitiate = ({ onSessionStarted }: GroupLiveInitiateProps): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const padding = !isTablet && isMobile ? 2 : 4;

    const [sessionName, setSessionName] = useState<string>("");
    const [participantCount, setParticipantCount] = useState<number>(10);
    const [nameError, setNameError] = useState<string>("");
    const [countError, setCountError] = useState<string>("");

    const validate = (): boolean => {
        let valid = true;
        if (sessionName.trim().length === 0) {
            setNameError("Session name is required.");
            valid = false;
        } else {
            setNameError("");
        }
        if (participantCount < 1 || participantCount > 60) {
            setCountError("Participant count must be between 1 and 60.");
            valid = false;
        } else {
            setCountError("");
        }
        return valid;
    };

    const handleStart = () => {
        if (!validate()) {
            return;
        }
        // TODO [VER-55]: Replace with real POST — /api/group-assessments/sessions
        // Body: { sessionName: sessionName.trim(), participantCount }
        // Response shape: GroupLiveSession (see VER-56-NOTES.md)
        const mockSession: GroupLiveSession = {
            ...MOCK_LIVE_SESSION,
            name: sessionName.trim(),
        };
        onSessionStarted(mockSession);
    };

    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile && <Box pt={8} />}

            {/* Page header */}
            <Box mb={4} display="flex" alignItems="center" gap={1}>
                <GroupOutlined color="primary" sx={{ fontSize: "2rem" }} />
                <Typography variant="h2" color="textPrimary">
                    Start group check-in
                </Typography>
            </Box>

            <Box maxWidth={600}>
                {/* Guidance card */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    Once started, participants join using a session code on their own devices. Individual responses are
                    never shown — only anonymous group-level results are displayed here.
                </Alert>

                {/* Form card */}
                <Card>
                    <CardContent>
                        <Typography variant="h5" mb={3}>
                            Session details
                        </Typography>

                        <Box mb={3}>
                            <TextField
                                label="Session name"
                                value={sessionName}
                                onChange={(e) => setSessionName(e.target.value)}
                                error={nameError.length > 0}
                                helperText={
                                    nameError.length > 0
                                        ? nameError
                                        : 'Shown to participants on the join screen (e.g. "Year 9 Wellbeing Check").'
                                }
                                fullWidth
                                inputProps={{ maxLength: 120 }}
                            />
                        </Box>

                        <Box mb={3}>
                            <TextField
                                label="Expected number of participants"
                                type="number"
                                value={participantCount}
                                onChange={(e) => {
                                    const parsed = parseInt(e.target.value, 10);
                                    setParticipantCount(isNaN(parsed) ? 1 : parsed);
                                }}
                                error={countError.length > 0}
                                helperText={
                                    countError.length > 0
                                        ? countError
                                        : "Used for the live roster — additional participants can still join."
                                }
                                fullWidth
                                inputProps={{ min: 1, max: 60 }}
                            />
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        {/* Patient-safety notice */}
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            WEMWBS is a wellbeing screening tool, not a clinical diagnostic. Do not share individual
                            scores with participants. Results are for group-level insight only. See the programme
                            consent notice before proceeding.
                        </Alert>

                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<PlayArrowOutlined />}
                            onClick={handleStart}
                            fullWidth
                        >
                            Start session
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default GroupLiveInitiate;
